/**
 * 🐋 Company Profile - Cetalingua FM
 * Target path: /src/components/advertiser/CompanyProfile.js
 * REPLACES existing CompanyProfile.js
 * 
 * CHANGES FROM ORIGINAL:
 * - Removed hardcoded SPECIES_OPTIONS (14 string-based species)
 * - Added SpeciesPicker component (full 88 species, numeric IDs)
 * - Added legacy species migration on load
 * - Removed old toggleSpecies function (SpeciesPicker handles it)
 * - Species now stored as numeric IDs (1-88) in Firestore
 */

import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase-config';
import SpeciesPicker from './SpeciesPicker';
import './SpeciesPicker.css';
import { migrateSpeciesIds } from '../../shared/speciesUtils';
import './CompanyProfile.css';

// REMOVED: Old hardcoded SPECIES_OPTIONS array
// Now using SpeciesPicker component with full 88 species from speciesData.js

const CompanyProfile = ({ userId, userData, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [uploadingImage, setUploadingImage] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    description: '',
    website: '',
    phone: '',
    email: '',
    location: {
      city: '',
      state: '',
      country: '',
    },
    species: [],    // Now numeric IDs (1-88)
    images: [],
  });

  // Load existing data
  useEffect(() => {
    if (userData) {
      let species = userData.species || [];

      // MIGRATION: Convert old string species IDs to numeric IDs
      if (species.length > 0 && typeof species[0] === 'string') {
        console.log('🐋 [CompanyProfile] Migrating legacy string species to numeric IDs');
        species = migrateSpeciesIds(species);
      }

      setFormData({
        companyName: userData.companyName || '',
        description: userData.description || '',
        website: userData.website || '',
        phone: userData.phone || '',
        email: userData.email || '',
        location: {
          city: userData.location?.city || '',
          state: userData.location?.state || '',
          country: userData.location?.country || '',
        },
        species: species,
        images: userData.images || [],
      });
    }
  }, [userData]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('location.')) {
      const locationField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          [locationField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // REMOVED: Old toggleSpecies function
  // SpeciesPicker handles selection internally and calls onChange with full array

  // Handle species change from SpeciesPicker
  const handleSpeciesChange = (newSpecies) => {
    setFormData(prev => ({ ...prev, species: newSpecies }));
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (formData.images.length + files.length > 5) {
      setMessage({ type: 'error', text: 'Maximum 5 images allowed' });
      return;
    }

    setUploadingImage(true);
    setMessage({ type: '', text: '' });

    try {
      const newImageUrls = [];

      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          throw new Error('Only image files are allowed');
        }
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('Image must be less than 5MB');
        }

        const timestamp = Date.now();
        const filename = `company-images/${userId}/${timestamp}_${file.name}`;
        const storageRef = ref(storage, filename);
        
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);
        newImageUrls.push(downloadUrl);
      }

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImageUrls]
      }));

      setMessage({ type: 'success', text: 'Image(s) uploaded successfully!' });
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to upload image' });
    } finally {
      setUploadingImage(false);
    }
  };

  // Remove image
  const handleRemoveImage = async (imageUrl, index) => {
    if (!window.confirm('Remove this image?')) return;

    try {
      try {
        const storageRef = ref(storage, imageUrl);
        await deleteObject(storageRef);
      } catch (storageError) {
        console.log('Could not delete from storage:', storageError);
      }

      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));

      setMessage({ type: 'success', text: 'Image removed' });
    } catch (error) {
      console.error('Error removing image:', error);
      setMessage({ type: 'error', text: 'Failed to remove image' });
    }
  };

  // Save profile
  const handleSave = async () => {
    if (!formData.companyName.trim()) {
      setMessage({ type: 'error', text: 'Company name is required' });
      return;
    }

    if (formData.species.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one species' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        companyName: formData.companyName.trim(),
        description: formData.description.trim(),
        website: formData.website.trim(),
        phone: formData.phone.trim(),
        location: formData.location,
        species: formData.species,  // Now numeric IDs
        images: formData.images,
        updatedAt: new Date(),
      });

      setMessage({ type: 'success', text: 'Profile saved successfully!' });
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: 'Failed to save profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="company-profile">
      <div className="profile-header">
        <h2>🏢 Company Profile</h2>
        <p>Tell whale watchers about your company</p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Basic Info Section */}
      <div className="profile-section">
        <h3>Basic Information</h3>
        
        <div className="form-group">
          <label htmlFor="companyName">Name / organisation *</label>
          <input
            id="companyName"
            name="companyName"
            type="text"
            value={formData.companyName}
            onChange={handleInputChange}
            placeholder="Pacific Whale Watch Tours"
            maxLength={100}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Tell potential customers about your whale watching experience, your boats, guides, and what makes your tours special..."
            rows={5}
            maxLength={1000}
          />
          <span className="char-count">{formData.description.length}/1000</span>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="url"
              value={formData.website}
              onChange={handleInputChange}
              placeholder="https://www.yourcompany.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>
      </div>

      {/* Location Section */}
      <div className="profile-section">
        <h3>📍 Location</h3>
        
        <div className="form-row three-col">
          <div className="form-group">
            <label htmlFor="location.city">City</label>
            <input
              id="location.city"
              name="location.city"
              type="text"
              value={formData.location.city}
              onChange={handleInputChange}
              placeholder="Monterey"
            />
          </div>

          <div className="form-group">
            <label htmlFor="location.state">State/Province</label>
            <input
              id="location.state"
              name="location.state"
              type="text"
              value={formData.location.state}
              onChange={handleInputChange}
              placeholder="California"
            />
          </div>

          <div className="form-group">
            <label htmlFor="location.country">Country</label>
            <input
              id="location.country"
              name="location.country"
              type="text"
              value={formData.location.country}
              onChange={handleInputChange}
              placeholder="USA"
            />
          </div>
        </div>
      </div>

      {/* Species Section — NOW USES SPECIESPICKER */}
      <div className="profile-section">
        <h3>🐋 Species You Feature</h3>
        <p className="section-desc">Select all species that can be seen on your tours</p>
        
        <SpeciesPicker
          selectedSpecies={formData.species}
          onChange={handleSpeciesChange}
          disabled={saving}
          showScientific={true}
        />
        
        {formData.species.length === 0 && (
          <p className="warning-text">⚠️ Please select at least one species</p>
        )}
      </div>

      {/* Images Section */}
      <div className="profile-section">
        <h3>📷 Company Photos</h3>
        <p className="section-desc">Add up to 5 photos of your boats, tours, or marine life (max 5MB each)</p>
        
        <div className="images-grid">
          {formData.images.map((imageUrl, index) => (
            <div key={index} className="image-item">
              <img src={imageUrl} alt={`Company ${index + 1}`} />
              <button
                type="button"
                className="remove-image-btn"
                onClick={() => handleRemoveImage(imageUrl, index)}
              >
                ×
              </button>
            </div>
          ))}
          
          {formData.images.length < 5 && (
            <label className="add-image-btn">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={uploadingImage}
              />
              {uploadingImage ? (
                <span className="uploading">Uploading...</span>
              ) : (
                <>
                  <span className="plus">+</span>
                  <span className="label">Add Photo</span>
                </>
              )}
            </label>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="profile-actions">
        <button
          className="save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : '💾 Save Profile'}
        </button>
      </div>
    </div>
  );
};

export default CompanyProfile;
